import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Timer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const IDLE_MS = 30 * 60 * 1000;
const WARN_MS = 2 * 60 * 1000;
const KEY = "pi_last_activity";
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

const lastActivity = () => Number(localStorage.getItem(KEY)) || Date.now();

// Déconnexion automatique après 30 min sans activité (synchronisée entre les onglets)
export const IdleLogout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [left, setLeft] = useState(null);
  const lastWrite = useRef(0);

  useEffect(() => {
    if (!localStorage.getItem(KEY)) localStorage.setItem(KEY, String(Date.now()));
    const touch = () => {
      if (Date.now() - lastWrite.current > 5000) {
        lastWrite.current = Date.now();
        localStorage.setItem(KEY, String(Date.now()));
      }
    };
    EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const timer = setInterval(async () => {
      const remain = IDLE_MS - (Date.now() - lastActivity());
      if (remain <= 0) {
        clearInterval(timer);
        await logout("inactivite");
        toast.info("Session fermée après 30 minutes d'inactivité.");
        navigate("/login");
      } else {
        setLeft(remain <= WARN_MS ? remain : null);
      }
    }, 1000);
    return () => {
      clearInterval(timer);
      EVENTS.forEach((e) => window.removeEventListener(e, touch));
    };
  }, [logout, navigate]);

  const stay = () => { localStorage.setItem(KEY, String(Date.now())); setLeft(null); };
  const s = Math.ceil((left || 0) / 1000);

  return (
    <Dialog open={left !== null} onOpenChange={(o) => { if (!o) stay(); }}>
      <DialogContent className="max-w-sm" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Timer className="h-5 w-5 text-amber-600" />Toujours là ?</DialogTitle>
          <DialogDescription>Par sécurité, votre session se fermera dans <b data-testid="idle-countdown">{Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}</b> faute d'activité.</DialogDescription>
        </DialogHeader>
        <Button onClick={stay} data-testid="idle-stay-btn">Rester connecté</Button>
      </DialogContent>
    </Dialog>
  );
};
